import React, { useEffect, useState } from "react";

// material-ui
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  OutlinedInput,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";

// third party
import { Formik } from "formik";

// project import
import AnimateButton from "../@extended/AnimateButton";

// assets
import { Country } from "country-state-city";
import { Dayjs } from "dayjs";
import NumberFormat from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import {
  ADMIN,
  CUSTOMER,
  DRIVER,
  EXTERNAL_PAYMENT,
  GATEWAY_PAYMENT,
  PAYMENT_TYPES,
  STAFF,
  SUPER_ADMIN,
} from "../../config/constants";
import {
  formatNumberWithThousandsSeparator,
  getStateOfCountryByCode,
  properlizeWords,
  startWithUpperCase,
} from "../../helpers";
import { FormErrContainer } from "../../pages/auth/Style";
import socket, {
  DEDICATION_TO_USER,
  ORDER_REQUEST_ASSIGNED,
} from "../../socket";
import { IAuthState, IUser } from "../../stateManagement/reducers/authReducer";
import {
  clearCart,
  ICartState,
  removeItemByRentalItemId,
  setCartItem,
} from "../../stateManagement/reducers/cartReducer";
import { ICompanyBankAccountsState } from "../../stateManagement/reducers/companyBankAccountsReducer";
import {
  IEnumCustomer,
  tryFetchMainCustomer,
} from "../../stateManagement/reducers/customersReducer";
import {
  clearRegOrderErrs,
  clearRegOrderFieldErr,
  IOrdersState,
  resetRegisteredOrder,
  tryFetchRecentOrders,
  tryRegOrder,
  tryResetRegOrderFormErr,
} from "../../stateManagement/reducers/ordersReducer";
import { IRentalItem } from "../../stateManagement/reducers/rentalItemsReducer";
import {
  ISite,
  ISitesState,
  tryFetchSites,
} from "../../stateManagement/reducers/sitesReducer";
import { IStaff } from "../../stateManagement/reducers/staffsReducer";
import { IRootReducerT } from "../../stateManagement/reduxStore";
import { SiteLevel } from "../../types/SiteLevel";
import IconText from "../IconText";
import CustomersMenu from "../InputFields/CustomersMenu";
import StaffsMenu from "../InputFields/StaffsMenu";
import { LocationPicker } from "../LocationPicker/LocationPicker";
import PopUp from "../PopUp/PopUp";

// ============================|| FIREBASE - REGISTER ||============================ //

const deliveryDurationPrefix = process.env.REACT_APP_DELIVERY_DURATION_PREFIX;

interface IFormProps {
  customerId: string;
  setOpened: (truth: boolean) => void;
}

const Form: React.FC<IFormProps> = ({ customerId, setOpened }) => {
  const [level, setLevel] = useState<{
    label: string;
    color: string;
  } | null>(null);

  const dispatch = useDispatch<any>();

  const { companyBankAccounts } = useSelector<
    IRootReducerT,
    ICompanyBankAccountsState
  >((state) => state.companyBankAccounts);
  const { regOrderErrs, registeringOrder, registeredOrder } = useSelector<
    IRootReducerT,
    IOrdersState
  >((state) => state.orders);
  const { authToken, user } = useSelector<IRootReducerT, IAuthState>(
    (state) => state.auth,
  );
  const { cart } = useSelector<IRootReducerT, ICartState>(
    (state) => state.cart,
  );

  const { sites } = useSelector<IRootReducerT, ISitesState>(
    (state) => state.sites,
  );

  const [customer, setCustomer] = useState<IEnumCustomer | null>(null);
  const [selectCustomer, setSelectCustomer] = useState(
    customerId === "" ? true : false,
  );

  const [staff, setStaff] = useState<IStaff | null>(null);
  const [staffId, setStaffId] = useState("");
  const [newItem, setNewItem] = useState<IRentalItem | null>(null);
  const [items, setItems] = useState<IRentalItem[]>([]);
  const [itemsCounts, setItemsCounts] = useState<number[]>([]);
  const [rentalItems, setRentalItems] = useState<
    { _id: string; count: string }[]
  >([]);

  const [site, setSite] = useState<ISite | null>(null);

  const [daysCount, setDaysCount] = useState("1");
  const [transportationFee, setTransportationFee] = useState("0");

  const [paymentOptions, setPaymentOptions] = useState(PAYMENT_TYPES);
  const [paymentTypes, setPaymentTypes] = useState<string[]>([GATEWAY_PAYMENT]);

  const [showBankAccFields, setShowBankAccFields] = useState(false);
  const [bankName, setBankName] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [remarks, setRemarks] = useState("");
  const [discount, setDiscount] = useState("");

  const countryCode = "NG";
  const provinces = ["LA"];
  const [province, setProvince] = useState("LA");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");

  const [deliveryDueDate, setDeliveryDueDate] = useState<Dayjs | null>(null);
  const defaultDeliveryDuration = "3 to 5 working days";

  const [noEarlyRefund, setNoEarlyRefund] = useState(true);

  const getItemsCost = () => {
    let total = 0;

    for (let i = 0; i < cart.items.length; i++) {
      total += cart.items[i].rentalItem.pricePerDay * cart.items[i].count;
    }

    return total;
  };

  const getTotalCost = () => {
    return parseInt(transportationFee) + getItemsCost();
  };

  const onFieldChange = (field: string) => {
    dispatch(clearRegOrderFieldErr(field));

    if (regOrderErrs.form) {
      dispatch(tryResetRegOrderFormErr());
    }
  };

  const rmvPaymentOption = (option: string) => {
    let newOptions: string[] = [];
    let itr = 0;

    for (const paymentOption of paymentOptions) {
      if (option === paymentOption) {
        continue;
      }
      newOptions[itr++] = paymentOption;
    }

    // setPaymentOptions(newOptions);
  };

  const [paymentLink, setPaymentLink] = useState<string>("");

  useEffect(() => {
    if (paymentTypes.length > 1) {
      const type = [paymentTypes[1]];

      setPaymentTypes(type);
    }
  }, [paymentTypes]);

  useEffect(() => {
    if (!staff && user?.role?.name === STAFF) {
      const usr: IUser = user as IUser;

      setStaff(usr as IStaff);
    }
  }, []);

  const [isInitRender0, setIsInitRender0] = useState(true);
  useEffect(() => {
    if (isInitRender0) {
      setIsInitRender0(false);
      dispatch(clearRegOrderFieldErr("customerId"));
      return;
    }

    if (regOrderErrs.customerId) {
      alert(`Error: ${regOrderErrs.customerId}`);

      dispatch(clearRegOrderFieldErr("customerId"));
    }
  }, [regOrderErrs.customerId]);

  const [isInitRender, setIsInitRender] = useState(true);

  useEffect(() => {
    if (isInitRender) {
      dispatch(resetRegisteredOrder());
      setIsInitRender(false);
      return;
    }

    if (registeredOrder) {
      dispatch(tryFetchRecentOrders({ authToken: authToken ?? "" }));

      if (customer || customerId !== "") {
        dispatch(
          tryFetchMainCustomer({
            authToken: authToken ?? "",
            customerId: customer ? customer?._id : customerId,
          }),
        );
      }

      socket.emit(DEDICATION_TO_USER, staffId);
      socket.emit(ORDER_REQUEST_ASSIGNED, staffId);

      alert("Order Created. You are now being redirected to the payment page.");

      dispatch(clearCart());

      setOpened(false);
    }
  }, [registeredOrder]);

  useEffect(() => {
    for (const paymentType of paymentTypes) {
      if (paymentType === EXTERNAL_PAYMENT) {
        setShowBankAccFields(true);
        return;
      }
    }

    if (showBankAccFields) {
      setShowBankAccFields(false);
    }
  }, [paymentTypes]);

  useEffect(() => {
    let shouldRmvExternalPayment = false;
    let shouldRmvGatewayPayment = false;

    for (const paymentType of paymentTypes) {
      if (paymentType === GATEWAY_PAYMENT) {
        shouldRmvExternalPayment = true;
        break;
      }

      if (paymentType === EXTERNAL_PAYMENT) {
        shouldRmvGatewayPayment = true;
        break;
      }
    }

    if (shouldRmvExternalPayment) {
      rmvPaymentOption(EXTERNAL_PAYMENT);
    } else if (shouldRmvGatewayPayment) {
      rmvPaymentOption(GATEWAY_PAYMENT);
    } else if (
      !shouldRmvExternalPayment &&
      !shouldRmvGatewayPayment &&
      paymentOptions.length === 2
    ) {
      setPaymentOptions(PAYMENT_TYPES);
    }
  }, [paymentTypes]);

  useEffect(() => {
    dispatch(clearRegOrderErrs());
  }, []);

  const [isInitRender2, setIsInitRender2] = useState(true);
  useEffect(() => {
    if (isInitRender2) {
      setIsInitRender2(false);
      return;
    }

    if (regOrderErrs.form) {
      alert(
        `${regOrderErrs.form}${
          user?.role?.name === CUSTOMER
            ? " If this error persists, please contact one of our administrators."
            : ""
        }`,
      );
    }
  }, [regOrderErrs.form]);

  useEffect(() => {
    if (staff) {
      setStaffId(staff._id);
      onFieldChange("staffId");
    }
  }, [staff]);

  const [toggled, setToggled] = useState(false);
  const toggle = () => {
    if (toggled) {
      setToggled(false);
    } else {
      setToggled(true);
    }
  };

  useEffect(() => {
    if (site) {
      toggle();
      onFieldChange("siteId");
    }
  }, [site]);

  useEffect(() => {
    if (customer) {
      toggle();
      onFieldChange("customerId");
    }
  }, [customer]);

  useEffect(() => {
    if (showBankAccFields && companyBankAccounts.length > 0) {
      const bankDetails = companyBankAccounts[0];
      setBankName(bankDetails.bankName);
      setAccountName(bankDetails.accountName);
      setAccountNumber(bankDetails.accountNumber);
    }
  }, [showBankAccFields, companyBankAccounts]);

  useEffect(() => {
    if (paymentLink !== "" && paymentLink) {
      window.open(paymentLink, "_self");
    }
  }, [paymentLink]);

  useEffect(() => {
    if (!cart.items.length) {
      setOpened(false);
    }
  }, [cart.items]);

  useEffect(() => {
    if (!sites.length) {
      dispatch(
        tryFetchSites({ authToken: authToken ?? "", ownerType: CUSTOMER }),
      );
    }
  }, []);

  useEffect(() => {
    let matchingSites: ISite[] = [];

    if (province !== "" && city !== "" && sites.length) {
      matchingSites = sites.filter(
        (site) => site.level === SiteLevel.CITY && site.city === city,
      );
    }

    if (!matchingSites.length && province !== "" && sites.length) {
      matchingSites = sites.filter((site) => {
        return Boolean(
          site.level === SiteLevel.STATE && site.state === province,
        );
      });
    }

    if (matchingSites.length) {
      setTransportationFee(String(matchingSites[0]?.deliveryFee ?? "0"));
      setSite(matchingSites[0]);
    } else {
      setTransportationFee("0");
      setSite(null);
    }
  }, [province, city, sites]);

  return (
    <>
      <Formik
        initialValues={{
          submit: null,
        }}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            setStatus({ success: false });
            setSubmitting(false);
          } catch (err: any) {
            console.error(err);
            setStatus({ success: false });
            setErrors({ submit: err.message });
            setSubmitting(false);
          }

          const data = {
            userIsCustomer: user?.role?.name === CUSTOMER ? true : undefined,
            customerId: selectCustomer ? (customer?._id ?? "") : customerId,
            staffId: user?.role?.name === CUSTOMER ? undefined : staffId,
            rentalItems: cart.items.map((cartItem) => ({
              _id: cartItem.rentalItem._id,
              count: cartItem.count.toString(),
            })),
            siteId: site?._id ?? "",
            address,
            city,
            state: getStateOfCountryByCode(countryCode, province),
            country: Country.getCountryByCode(countryCode)?.name ?? "",
            rentalDaysCount: daysCount,
            deliveryDueDate: deliveryDueDate
              ? deliveryDueDate.format("YYYY-MM-DD")
              : "3000-10-23",
            transportationFee:
              transportationFee === "" ? "0" : transportationFee,
            paymentTypes,
            bankName,
            accountName,
            accountNumber,
            totalCost: getTotalCost() ? getTotalCost().toString() : undefined,
            discount: discount ? discount : undefined,
            noEarlyRefund,
            remarks,
          };

          dispatch(
            tryRegOrder({
              data,
              authToken: authToken ?? "",
              setPaymentLink,
            }),
          );
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            {regOrderErrs.form && (
              <FormErrContainer>
                <FormHelperText error id="helper-text-firstName-signup">
                  {regOrderErrs.form}
                </FormHelperText>
              </FormErrContainer>
            )}
            {registeredOrder && (
              <FormErrContainer>
                <FormHelperText
                  error={false}
                  sx={{ color: "success" }}
                  id="helper-text-firstName-signup"
                >
                  Successful!!
                </FormHelperText>
              </FormErrContainer>
            )}
            {user?.role?.name !== CUSTOMER && selectCustomer && (
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="rentalDays">
                      Assigned Customer*
                    </InputLabel>
                    {customer && (
                      <IconText
                        iconSrc={customer?.imgSrc ?? ""}
                        topText={properlizeWords(
                          `${customer.firstName} ${customer.lastName}`,
                        )}
                        bottomText={customer.email}
                        error={Boolean(regOrderErrs.customerId)}
                      />
                    )}
                    {regOrderErrs.customerId && (
                      <FormHelperText error id="helper-text-firstName-signup">
                        {regOrderErrs.customerId}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
                {(user?.role?.name === ADMIN ||
                  user?.role?.name === SUPER_ADMIN ||
                  user?.role?.name === STAFF) && (
                  <Grid item xs={12} container justifyContent="flex-end">
                    <Grid item>
                      <CustomersMenu
                        setCustomer={setCustomer}
                        reAssigning={customer ? true : false}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            )}
            {user?.role?.name !== CUSTOMER && user?.role?.name !== STAFF && (
              <Grid container spacing={1} mt={selectCustomer ? 2 : 0}>
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="rentalDays">
                      Assigned Staff*
                    </InputLabel>
                    {staff && (
                      <IconText
                        iconSrc={staff?.imgSrc ?? ""}
                        topText={properlizeWords(
                          `${staff.firstName} ${staff.lastName}`,
                        )}
                        bottomText={staff.email}
                        error={Boolean(regOrderErrs.staffId)}
                      />
                    )}
                    {regOrderErrs.staffId && (
                      <FormHelperText error id="helper-text-firstName-signup">
                        {regOrderErrs.staffId}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
                {(user?.role?.name === ADMIN ||
                  user?.role?.name === SUPER_ADMIN) && (
                  <Grid item xs={12} container justifyContent="flex-end">
                    <Grid item>
                      <StaffsMenu
                        setStaff={setStaff}
                        reAssigning={staff ? true : false}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            )}
            <Grid
              container
              spacing={2}
              mt={user?.role?.name !== CUSTOMER ? 2 : 0}
            >
              {user?.role?.name !== CUSTOMER && (
                <Grid item xs={12}>
                  <Divider>
                    <Typography variant="h4">Order Details</Typography>
                  </Divider>
                </Grid>
              )}
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="rentalDays">
                    Product/Service(s)*
                  </InputLabel>
                  <TableContainer
                    sx={{
                      width: "100%",
                      overflowX: "auto",
                      position: "relative",
                      display: "block",
                      maxWidth: "100%",
                      "& td, & th": { whiteSpace: "nowrap" },
                    }}
                  >
                    <Table
                      aria-labelledby="tableTitle"
                      sx={{
                        "& .MuiTableCell-root:first-of-type": {
                          pl: 2,
                        },
                        "& .MuiTableCell-root:last-of-type": {
                          pr: 3,
                        },
                      }}
                    >
                      <TableBody>
                        {cart.items.map((item, index) => {
                          return (
                            <TableRow
                              hover
                              style={{ position: "relative" }}
                              role="button"
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                              }}
                              tabIndex={-1}
                              key={item.rentalItem._id}
                            >
                              <TableCell
                                component="th"
                                align="left"
                                scope="row"
                                color="textSecondary"
                              >
                                <Typography
                                  variant="body1"
                                  color="textSecondary"
                                >
                                  <IconText
                                    iconSrc={item.rentalItem.imgSrc}
                                    variant="rounded"
                                    topText={startWithUpperCase(
                                      item.rentalItem.name,
                                    )}
                                    bottomText=""
                                    sxIcon={{
                                      height: "2.5rem",
                                      width: "2.5rem",
                                    }}
                                    is4Item
                                  />
                                </Typography>
                              </TableCell>
                              <TableCell align="left">
                                <Typography
                                  variant="body1"
                                  color="textSecondary"
                                >
                                  <OutlinedInput
                                    id={`item-count-${index}`}
                                    type="number"
                                    value={item.count}
                                    name="itemCount"
                                    onBlur={handleBlur}
                                    onChange={(e) => {
                                      if (parseInt(e.target.value) < 1) {
                                        alert(
                                          "The selected number of an item cannot be lesser than 1",
                                        );

                                        // update the cart-item-count

                                        return;
                                      }

                                      if (
                                        parseInt(e.target.value) >
                                        item.rentalItem.availableCount
                                      ) {
                                        alert(
                                          `Input-error: for ${item.rentalItem.name}: only ${item.rentalItem.availableCount} available, but ${e.target.value} selected.`,
                                        );
                                      }
                                      // onFieldChange('firstName')
                                      dispatch(
                                        setCartItem({
                                          rentalItem: item.rentalItem,
                                          count: Number(
                                            Number(e.target.value).toFixed(0),
                                          ),
                                        }),
                                      );
                                    }}
                                    sx={{
                                      maxWidth: "5rem",
                                      minWidth: "5rem",
                                      maxHeight: "4rem",
                                      padding: "0",
                                    }}
                                    placeholder="count"
                                    fullWidth
                                    error={Boolean(
                                      item.count >
                                      item.rentalItem.availableCount,
                                    )}
                                  />
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body1"
                                  color="textSecondary"
                                >
                                  <NumberFormat
                                    displayType="text"
                                    thousandSeparator
                                    prefix="₦"
                                    value={
                                      item.rentalItem.pricePerDay * item.count
                                    }
                                  />
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body1"
                                  color="textSecondary"
                                >
                                  <AnimateButton>
                                    <Button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        dispatch(
                                          removeItemByRentalItemId(
                                            item.rentalItem._id,
                                          ),
                                        );
                                      }}
                                      component={Link}
                                      target="_blank"
                                      href="#"
                                      variant="contained"
                                      color="error"
                                      size="small"
                                      sx={{
                                        backgroundColor: "secondary.main",
                                        borderColor: "secondary.main",

                                        "&:hover ": {
                                          backgroundColor: "secondary.dark",
                                          borderColor: "secondary.dark",
                                        },
                                      }}
                                    >
                                      Remove Item
                                    </Button>
                                  </AnimateButton>
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {regOrderErrs.rentalItems && (
                    <FormHelperText
                      error
                      id="helper-text-rental-items-reg-order"
                    >
                      {regOrderErrs.rentalItems}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              {/* <Grid item xs={12} container justifyContent="flex-end" sx={{}}>
                <AvailableRentalItemsMenu
                  btnText="Add Product/Service"
                  setItem={setNewItem}
                />
              </Grid> */}
            </Grid>
            <Grid container spacing={3} mt={2}>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="rentalDays">
                    Select Location 🇳🇬*
                  </InputLabel>
                  {/* {site && (
                    <IconText
                      iconSrc={"/sdsdcdslk"}
                      topText={properlizeWords(site.name)}
                      bottomText={
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          maxWidth={"15rem"}
                          noWrap
                        >
                          {site.address}
                        </Typography>
                      }
                      error={Boolean(regOrderErrs.siteId)}
                      variant="rounded"
                      svg={<LocationOn />}
                    />
                  )} */}
                  <LocationPicker
                    countryCode={countryCode}
                    province={province}
                    city={city}
                    address={address}
                    setProvince={(newProvince: string) => {}}
                    provinces={provinces}
                    setCity={setCity}
                    setAddress={setAddress}
                    onChange={onFieldChange}
                    errors={regOrderErrs}
                  />
                  {regOrderErrs.siteId && (
                    <FormHelperText error id="helper-text-firstName-signup">
                      {regOrderErrs.siteId}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              {/* <Grid item xs={12} container justifyContent="flex-end">
                <Grid item>
                  <SitesMenu
                    setSite={setSite}
                    reAssigning={site ? true : false}
                    owner={{
                      type: CUSTOMER,
                      id: customer ? customer._id : customerId,
                    }}
                  />
                </Grid>
              </Grid> */}
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="deliveryDate">
                    Date For Delivery*
                  </InputLabel>
                  {/* <DatePicker
                    value={deliveryDueDate}
                    onChange={(newValue) => {
                      setDeliveryDueDate(newValue);
                      onFieldChange("deliveryDueDate");
                    }}
                    slotProps={{
                      popper: {
                        style: {
                          zIndex: "1000000000000000000000000000000000",
                          position: "fixed",
                        },
                      },
                    }}
                    minDate={dayjs()}
                  /> */}
                  <Typography sx={{ opacity: "1", color: "secondary.main" }}>
                    {deliveryDurationPrefix}{" "}
                    {site?.deliveryDuration ?? defaultDeliveryDuration}
                  </Typography>
                  {regOrderErrs.deliveryDueDate && (
                    <FormHelperText error id="helper-text-email-signup">
                      {regOrderErrs.deliveryDueDate}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              {/* <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="rentalDays">Number of days*</InputLabel>
                  <OutlinedInput
                    id="rentalDays"
                    type="number"
                    value={daysCount}
                    name="daysCount"
                    onBlur={handleBlur}
                    onChange={(e) => {
                      setDaysCount(e.target.value);
                      onFieldChange("rentalDaysCount");
                    }}
                    sx={{ maxWidth: "5rem" }}
                    placeholder="1"
                    fullWidth
                    error={Boolean(regOrderErrs.rentalDaysCount)}
                  />
                  {regOrderErrs.rentalDaysCount && (
                    <FormHelperText error id="helper-text-firstName-signup">
                      {regOrderErrs.rentalDaysCount}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid> */}
              {user?.role?.name !== CUSTOMER && (
                <>
                  <Grid item xs={12}>
                    <Stack spacing={1}>
                      <InputLabel htmlFor="transportation-fee">
                        Transportation Fee (₦) (Optional)
                      </InputLabel>
                      <OutlinedInput
                        error={Boolean(regOrderErrs.transportationFee)}
                        id="transportation-fee"
                        type="number"
                        value={transportationFee}
                        name="lastName"
                        onBlur={handleBlur}
                        onChange={(e) => {
                          // handleChange(e);
                          setTransportationFee(e.target.value);
                          onFieldChange("transportationFee");
                        }}
                        sx={{ maxWidth: "5rem" }}
                        placeholder="00.00"
                        inputProps={{}}
                      />
                      {regOrderErrs.transportationFee && (
                        <FormHelperText error id="helper-text-lastName-signup">
                          {regOrderErrs.transportationFee}
                        </FormHelperText>
                      )}
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack spacing={1}>
                      <InputLabel htmlFor="remarks">
                        Remarks (Optional)
                      </InputLabel>
                      <OutlinedInput
                        multiline
                        rows={3}
                        maxRows={Infinity}
                        id="remarks"
                        type="text"
                        value={remarks}
                        name="remarks"
                        onBlur={handleBlur}
                        onChange={(e) => {
                          setRemarks(e.target.value);
                          onFieldChange("remarks");
                        }}
                        placeholder="..."
                        fullWidth
                        error={Boolean(regOrderErrs.remarks)}
                      />
                      {regOrderErrs.remarks && (
                        <FormHelperText error id="remarks-error">
                          {regOrderErrs.remarks}
                        </FormHelperText>
                      )}
                    </Stack>
                  </Grid>
                </>
              )}

              {user?.role?.name !== DRIVER && (
                <>
                  {user?.role?.name !== CUSTOMER && (
                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="discount">
                          Discount (₦) (Optional)
                        </InputLabel>
                        <OutlinedInput
                          error={Boolean(regOrderErrs.discount)}
                          id="discount"
                          type="number"
                          value={discount}
                          name="discount"
                          onBlur={handleBlur}
                          onChange={(e) => {
                            // handleChange(e);
                            setDiscount(e.target.value);
                            onFieldChange("discount");
                          }}
                          sx={{ maxWidth: "5rem" }}
                          placeholder="00.00"
                          inputProps={{}}
                        />
                        {regOrderErrs.discount && (
                          <FormHelperText
                            error
                            id="helper-text-lastName-signup"
                          >
                            {regOrderErrs.discount}
                          </FormHelperText>
                        )}
                      </Stack>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Grid
                      container
                      direction="row"
                      justifyContent="space-between"
                      mt={2}
                    >
                      <Grid item>
                        <Typography
                          variant="h5"
                          color="textSecondary"
                          sx={{ fontSize: "1rem" }}
                        >
                          Sub-Total:
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography
                          variant="h5"
                          color="textSecondary"
                          sx={{ fontSize: "1rem" }}
                        >
                          <NumberFormat
                            displayType="text"
                            thousandSeparator
                            prefix="₦"
                            value={formatNumberWithThousandsSeparator(
                              getItemsCost(),
                            )}
                          />
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  {Boolean(parseInt(transportationFee)) && (
                    <Grid item xs={12}>
                      <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        mt={0}
                      >
                        <Grid item>
                          <Typography
                            variant="h5"
                            color="textSecondary"
                            sx={{ fontSize: "1rem" }}
                          >
                            Transportation Fee:
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography
                            variant="h5"
                            color="textSecondary"
                            sx={{ fontSize: "1rem" }}
                          >
                            <NumberFormat
                              displayType="text"
                              thousandSeparator
                              prefix="₦"
                              value={transportationFee}
                            />
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Grid
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item>
                        <Typography
                          variant="h5"
                          color="textSecondary"
                          sx={{ fontSize: "1rem" }}
                        >
                          Total:
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography
                          variant="h5"
                          color="textSecondary"
                          sx={{ fontSize: "1rem" }}
                        >
                          <NumberFormat
                            displayType="text"
                            thousandSeparator
                            prefix="₦"
                            value={formatNumberWithThousandsSeparator(
                              getTotalCost() -
                                parseInt(discount !== "" ? discount : "0"),
                            )}
                          />
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )}
              {getTotalCost() > 0 && (
                <Grid item xs={12} container>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  {/* <Grid item xs={12} mt={1}>
                    <Stack spacing={1}>
                      <InputLabel htmlFor="paymentTypes">
                        Payment Method(s)*
                      </InputLabel>
                      <CustomMultipleSelect
                        id="paymentTypes"
                        placeHolder="Select"
                        value={paymentTypes}
                        setValue={setPaymentTypes}
                        options={paymentOptions}
                        onBlur={handleBlur}
                        error={Boolean(regOrderErrs.paymentTypes)}
                        onFieldChange={() => onFieldChange("paymentTypes")}
                      />
                      {regOrderErrs.paymentTypes && (
                        <FormHelperText error id="helper-text-order-request">
                          {regOrderErrs.paymentTypes}
                        </FormHelperText>
                      )}
                    </Stack>
                  </Grid> */}
                  {showBankAccFields && user?.role?.name !== CUSTOMER && (
                    <Grid item xs={12}>
                      <Grid container spacing={2} mt={0}>
                        <Grid item xs={12}>
                          <Divider textAlign="left">
                            <Typography variant="h5">
                              Bank Account Details
                            </Typography>
                          </Divider>
                        </Grid>
                        <Grid item xs={12}>
                          <Stack spacing={1}>
                            <InputLabel htmlFor="bankName">
                              Bank Name*
                            </InputLabel>
                            <OutlinedInput
                              error={Boolean(regOrderErrs.bankName)}
                              id="bankName"
                              type="text"
                              value={bankName}
                              name="bankName"
                              onBlur={handleBlur}
                              onChange={(e) => {
                                // handleChange(e);
                                setBankName(e.target.value);
                                onFieldChange("bankName");
                              }}
                              placeholder="Bank of America"
                              inputProps={{}}
                            />
                            {regOrderErrs.bankName && (
                              <FormHelperText
                                error
                                id="helper-text-bankName-register"
                              >
                                {regOrderErrs.bankName}
                              </FormHelperText>
                            )}
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          <Stack spacing={1}>
                            <InputLabel htmlFor="accountName">
                              Account Name*
                            </InputLabel>
                            <OutlinedInput
                              error={Boolean(regOrderErrs.accountName)}
                              id="accountName"
                              type="text"
                              value={accountName}
                              name="accountName"
                              onBlur={handleBlur}
                              onChange={(e) => {
                                // handleChange(e);
                                setAccountName(e.target.value);
                                onFieldChange("accountName");
                              }}
                              placeholder="John Doe"
                              inputProps={{}}
                            />
                            {regOrderErrs.accountName && (
                              <FormHelperText
                                error
                                id="helper-text-accountName-reg"
                              >
                                {regOrderErrs.accountName}
                              </FormHelperText>
                            )}
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          <Stack spacing={1}>
                            <InputLabel htmlFor="accountName">
                              Account Number*
                            </InputLabel>
                            <OutlinedInput
                              error={Boolean(regOrderErrs.accountNumber)}
                              id="accountName"
                              type="number"
                              value={accountNumber}
                              name="accountNumber"
                              onBlur={handleBlur}
                              onChange={(e) => {
                                // handleChange(e);
                                setAccountNumber(e.target.value);
                                onFieldChange("accountNumber");
                              }}
                              placeholder="0130000555"
                              inputProps={{}}
                            />
                            {regOrderErrs.accountNumber && (
                              <FormHelperText
                                error
                                id="helper-text-accountName-reg"
                              >
                                {regOrderErrs.accountNumber}
                              </FormHelperText>
                            )}
                          </Stack>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              )}
              {/* <Grid item xs={12} sx={{ mt: -1 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={noEarlyRefund}
                                                onChange={(event) => setNoEarlyRefund(event.target.checked)}
                                                name="checked"
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                        label={<Typography variant="h6">No Refund on Early Return</Typography>}
                                    />
                                </Stack>
                            </Grid> */}
              {errors.submit && (
                <Grid item xs={12}>
                  <FormHelperText error>{errors.submit}</FormHelperText>
                </Grid>
              )}
              <Grid item xs={12}>
                <AnimateButton>
                  <Button
                    disableElevation
                    disabled={isSubmitting || registeringOrder}
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    color="primary"
                  >
                    {!registeringOrder && `Place Order`}
                    {registeringOrder && `Placing Order`}
                  </Button>
                </AnimateButton>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
      {registeringOrder && (
        <PopUp
          id="formPopUp5"
          notCloseable
          deployed={registeringOrder}
          setDeployed={(truth: boolean) => true}
        >
          <Box>
            <Stack alignItems="center" spacing={1}>
              <CircularProgress sx={{ color: "primary.main" }} />
              <Typography
                variant="body1"
                textAlign="center"
                sx={{ color: "background.paper" }}
              >
                Warning: don't refresh this page while it is loading.
              </Typography>
            </Stack>
          </Box>
        </PopUp>
      )}
    </>
  );
};

export default Form;
